import { isSvgImage } from '@/lib/assets-config';
import {
  type ExplicitImageSize,
  type RequestedImageSize,
  readImageSize,
} from '@/lib/blocks/image-size';
import { pickStringUnion, readRecord, readString } from '@/lib/prop-utils';
import type { CollectionCardAlign, CollectionCardField, CollectionImagePosition } from './blocks';

const COLLECTION_MEDIA_POSITION_VALUES = [
  'top',
  'left',
  'right',
  'background',
] as const satisfies readonly CollectionImagePosition[];
export type ExplicitCollectionMediaSize = ExplicitImageSize;
export type RequestedCollectionMediaSize = RequestedImageSize;
export type ResolvedCollectionMediaSize = ExplicitCollectionMediaSize;

// The media settings as written, where the size may still be left to us.
export interface CollectionMediaLayout {
  position: CollectionImagePosition;
  size: RequestedCollectionMediaSize;
}

/**
 * The same settings once every automatic value has been decided, so a renderer
 * never has to work one out.
 */
export interface ResolvedCollectionMediaLayout {
  position: CollectionImagePosition;
  size: ResolvedCollectionMediaSize;
  align: CollectionCardAlign;
}

type CollectionMediaFieldTraits = Pick<CollectionCardField, 'blockProps'> &
  Partial<Pick<CollectionCardField, 'kind'>>;

interface ResolveCollectionMediaLayoutOptions {
  layout: CollectionMediaLayout;
  mediaField?: CollectionMediaFieldTraits;
  mediaValue?: unknown;
  cardAlign: CollectionCardAlign;
}

// Reads the media settings, which are stored per variant.
// TODO(collection-media-contract): Move imagePosition and imageSize under media after a coordinated data migration.
export function parseCollectionMediaLayout(
  variantProps: Record<string, unknown>,
): CollectionMediaLayout {
  const position = pickStringUnion(
    readString(variantProps.imagePosition),
    COLLECTION_MEDIA_POSITION_VALUES,
    'top',
  );
  const size = readImageSize(variantProps.imageSize);
  return { position, size };
}

/**
 * Works out a size when none was chosen, based on how the image itself is set up
 * to be displayed.
 */
export function resolveCollectionMediaSize(
  size: RequestedCollectionMediaSize,
  mediaField?: CollectionMediaFieldTraits,
  mediaValue?: unknown,
): ResolvedCollectionMediaSize {
  if (size !== 'auto') return size;

  if (mediaField?.kind === 'icon') return 'extra-small';

  const blockProps = mediaField?.blockProps;
  const itemProps = readRecord(mediaValue);
  const shape = readString(itemProps?.shape) ?? readString(blockProps?.shape);
  const fit = readString(itemProps?.fit) ?? readString(blockProps?.fit);
  if (shape === 'circle') return 'compact';
  if (isSvgImage(mediaValue)) return 'extra-small';
  if (fit === 'contain') return 'compact';

  const aspect = readString(blockProps?.aspect);
  const generatedImageSize = readString(itemProps?.imageSize);
  const isPortraitOrSquareGeneration =
    generatedImageSize?.startsWith('portrait_') || generatedImageSize?.startsWith('square');
  if (aspect === 'portrait' || aspect === 'square' || isPortraitOrSquareGeneration) {
    return 'regular';
  }

  if (aspect === 'landscape' || aspect === 'wide' || aspect === 'ultrawide' || aspect === 'video') {
    return 'large';
  }

  return 'large';
}

// Turns the settings into the finished layout a card is drawn from.
export function resolveCollectionMediaLayout({
  layout,
  mediaField,
  mediaValue,
  cardAlign,
}: ResolveCollectionMediaLayoutOptions): ResolvedCollectionMediaLayout {
  const position =
    layout.position === 'background' && mediaField?.kind !== 'image' ? 'top' : layout.position;

  return {
    position,
    size: resolveCollectionMediaSize(layout.size, mediaField, mediaValue),
    align: cardAlign,
  };
}
